<template>
  <div class="orphans-page">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="mb-0">
          <i class="fas fa-unlink me-2"></i>
          {{ $t('Orphans') || 'Orphans' }}
        </h3>
        <button
          v-if="totalCount > 0"
          type="button"
          class="btn btn-danger"
          :disabled="loading || cleaning"
          @click="confirmClean"
        >
          <i class="fas fa-trash-alt me-1"></i>
          {{ $t('Clean all orphans') || 'Clean all orphans' }} ({{ totalCount }})
        </button>
      </div>
      <div class="card-body">
        <p class="text-muted">
          {{ $t('Orphans description') || 'Group model documents (e.g. payment_appliedto) that have no parent or whose parent document no longer exists.' }}
        </p>
        <p class="text-muted small">
          {{ $t('Payment expense mismatch description') || 'Payment ↔ expense: payment_appliedto links a payment to a shipping_expense, but the expense does not list that payment in payments[] (or the expense is missing).' }}
        </p>

        <ContentSkeleton v-if="loading" variant="cards" :rows="3" />

        <div
          v-else-if="totalCount === 0 && paymentExpenseMismatchCount === 0 && !loading"
          class="alert alert-success mb-0"
        >
          <i class="fas fa-check-circle me-2"></i>
          {{ $t('No orphans found') || 'No orphans found.' }}
        </div>

        <div v-else>
          <div
            v-for="group in byModel"
            :key="group.groupModelName"
            class="card mb-3"
          >
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <strong>{{ group.groupModelName }}</strong>
              <span class="badge bg-warning text-dark">{{ group.count }} {{ $t('orphans') || 'orphans' }}</span>
            </div>
            <div class="card-body py-2">
              <small class="text-muted d-block mb-2">
                {{ $t('Parent model') || 'Parent model' }}: {{ group.parentModelName }} ({{ group.fieldKey }})
              </small>
              <ul class="list-unstyled mb-0 small">
                <li v-for="orphan in group.orphans" :key="orphan._id" class="mb-2 d-flex align-items-center flex-wrap">
                  <span class="me-2">
                    <strong>{{ orphan.name || orphan._id }}</strong>
                    <span v-if="!orphan.name" class="text-muted ms-1">({{ orphan._id }})</span>
                  </span>
                  <span v-if="orphan.parent" class="text-muted me-2">— parent: {{ orphan.parent }}</span>
                  <span v-else class="text-danger me-2">— {{ $t('No parent') || 'No parent' }}</span>
                  <a
                    :href="getOrphanViewUrl(group.groupModelName, orphan._id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn-sm btn-outline-primary"
                  >
                    <i class="fas fa-external-link-alt me-1"></i>
                    {{ $t('View') || 'View' }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="paymentExpenseMismatchCount > 0" class="card mb-3 border-warning">
            <div class="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>
                <i class="fas fa-link-slash me-2"></i>
                {{ $t('Payment expense link issues') || 'Payment / shipping expense link issues' }}
              </strong>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="badge bg-warning text-dark">
                  {{ paymentExpenseMismatchCount }}
                  {{ $t('issues') || 'issues' }}
                </span>
                <button
                  v-if="fixablePaymentExpenseCount > 0"
                  type="button"
                  class="btn btn-sm btn-warning"
                  :disabled="loading || syncingPaymentLinks"
                  @click="confirmSyncPaymentLinks"
                >
                  <i class="fas fa-wrench me-1"></i>
                  {{ $t('Fix expense payment backlinks') || 'Fix expense payment backlinks' }}
                  ({{ fixablePaymentExpenseCount }})
                </button>
              </div>
            </div>
            <div class="card-body py-2">
              <ul class="list-unstyled mb-0 small">
                <li
                  v-for="row in paymentExpenseMismatches"
                  :key="row.paymentAppliedToId"
                  class="mb-3 pb-2 border-bottom"
                >
                  <div class="d-flex flex-wrap align-items-center gap-2">
                    <span
                      class="badge"
                      :class="row.kind === 'expense_missing' ? 'bg-danger' : 'bg-secondary'"
                    >
                      {{
                        row.kind === 'expense_missing'
                          ? ($t('Expense missing') || 'Expense missing')
                          : ($t('Payment not on expense') || 'Payment not on expense')
                      }}
                    </span>
                    <span>
                      <strong>{{ $t('Orphans expense payment label Payment') || 'Payment' }}:</strong>
                      {{ row.paymentName || row.paymentId }}
                    </span>
                    <span class="text-muted">({{ row.paymentId }})</span>
                    <span v-if="row.expenseName || row.expenseId">
                      — <strong>{{ $t('Orphans expense payment label Expense') || 'Expense' }}:</strong>
                      {{ row.expenseName || row.expenseId }}
                      <span v-if="row.expenseId" class="text-muted">({{ row.expenseId }})</span>
                    </span>
                  </div>
                  <div v-if="row.expensePayments && row.expensePayments.length" class="text-muted mt-1">
                    {{ $t('Current expense payments ids') || 'Current expense payments[]' }}:
                    {{ row.expensePayments.join(', ') }}
                  </div>
                  <div class="mt-1 d-flex flex-wrap gap-1">
                    <a
                      :href="getOrphanViewUrl('payment', row.paymentId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-outline-primary"
                    >
                      {{ $t('View payment') || 'View payment' }}
                    </a>
                    <a
                      v-if="row.expenseId && row.kind !== 'expense_missing'"
                      :href="getOrphanViewUrl('shipping_expenses', row.expenseId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-outline-primary"
                    >
                      {{ $t('View expense') || 'View expense' }}
                    </a>
                    <a
                      :href="getOrphanViewUrl('payment_appliedto', row.paymentAppliedToId)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-outline-secondary"
                    >
                      {{ $t('View applied row') || 'View applied row' }}
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "Orphans",
  components: { ContentSkeleton },
  data() {
    return {
      loading: true,
      cleaning: false,
      syncingPaymentLinks: false,
      byModel: [],
      totalCount: 0,
      paymentExpenseMismatches: [],
      paymentExpenseMismatchCount: 0,
    };
  },
  computed: {
    fixablePaymentExpenseCount() {
      return this.paymentExpenseMismatches.filter((r) => r.kind === "payment_missing_from_expense").length;
    },
  },
  mounted() {
    this.loadOrphans();
  },
  methods: {
    getOrphanViewUrl(modelName, itemId) {
      const base = (this.$router.options.base || "").replace(/\/$/, "");
      return `${base}/dynamic-model/${encodeURIComponent(modelName)}/view/${encodeURIComponent(itemId)}`;
    },
    async loadOrphans() {
      this.loading = true;
      try {
        const res = await wencesApi.get("/api/settings/orphans");
        if (res.data && res.data.success && res.data.data) {
          this.byModel = res.data.data.byModel || [];
          this.totalCount = res.data.data.totalCount || 0;
          this.paymentExpenseMismatches = res.data.data.paymentExpenseMismatches || [];
          this.paymentExpenseMismatchCount = res.data.data.paymentExpenseMismatchCount ?? this.paymentExpenseMismatches.length;
        }
      } catch (e) {
        this.$notify({
          type: "danger",
          message: this.$tn("Failed to load orphans") || "Failed to load orphans.",
        });
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    confirmClean() {
      const msg = this.$t("Are you sure you want to permanently delete all orphans?") ||
        "Are you sure you want to permanently delete all orphans?";
      if (!confirm(msg)) return;
      this.cleanOrphans();
    },
    confirmSyncPaymentLinks() {
      const msg =
        this.$t("Confirm fix expense payment links") ||
        "Add each missing payment id to shipping_expenses.payments for the linked expense?";
      if (!confirm(msg)) return;
      this.syncExpensePaymentLinks();
    },
    async syncExpensePaymentLinks() {
      this.syncingPaymentLinks = true;
      try {
        const res = await wencesApi.post("/api/settings/orphans/sync-expense-payment-links");
        if (res.data && res.data.success) {
          const d = res.data.data || {};
          this.$notify({
            type: "success",
            message:
              (this.$tn("Orphans fixed label") || "Fixed") +
              " " +
              (d.fixed ?? 0) +
              " / " +
              (d.fixable ?? 0) +
              " " +
              (this.$tn("expense payment links") || "expense payment links"),
          });
          await this.loadOrphans();
        } else {
          this.$notify({
            type: "danger",
            message: res.data?.error || this.$tn("Failed to sync links") || "Failed to sync links.",
          });
        }
      } catch (e) {
        this.$notify({
          type: "danger",
          message: e?.response?.data?.error || this.$tn("Failed to sync links") || "Failed to sync links.",
        });
        console.error(e);
      } finally {
        this.syncingPaymentLinks = false;
      }
    },
    async cleanOrphans() {
      this.cleaning = true;
      try {
        const res = await wencesApi.post("/api/settings/orphans/clean");
        if (res.data && res.data.success) {
          const n = res.data.data?.totalDeleted ?? 0;
          this.$notify({
            type: "success",
            message: (this.$tn("Deleted") || "Deleted") + " " + n + " " + (this.$tn("orphans") || "orphans"),
          });
          await this.loadOrphans();
        } else {
          this.$notify({
            type: "danger",
            message: res.data?.error || (this.$tn("Failed to clean orphans") || "Failed to clean orphans."),
          });
        }
      } catch (e) {
        this.$notify({
          type: "danger",
          message: e?.response?.data?.error || (this.$tn("Failed to clean orphans") || "Failed to clean orphans."),
        });
        console.error(e);
      } finally {
        this.cleaning = false;
      }
    },
  },
};
</script>

<style scoped>
.orphans-page .card-header h3 {
  font-size: 1.25rem;
}
</style>
